import { useState } from 'react';
import { Check, ChevronsUpDown, X, Plus } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from '@/components/ui/command';
import { Badge } from '@/components/ui/badge';
import { useLinkBuildingDomains, LinkBuildingDomain } from '@/hooks/useLinkBuildingDomains';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { useAuth } from '@/hooks/useAuth';
import { DialogTitleWithBug } from '@/components/ui/dialog-title-with-bug';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

interface DomainMultiSelectProps {
  brandId: string;
  selectedDomainIds: string[];
  onChange: (domainIds: string[]) => void;
}

export function DomainMultiSelect({ brandId, selectedDomainIds, onChange }: DomainMultiSelectProps) {
  const { session } = useAuth();
  const { createDomain } = useLinkBuildingDomains(brandId);
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [newDomain, setNewDomain] = useState({
    domain_name: '',
    traffic: '',
    dr: '',
  });

  // Fetch ALL domains without pagination for the selector
  const { data: allDomains = [] } = useQuery({
    queryKey: ['link-building-domains-all', brandId],
    queryFn: async () => {
      let allData: LinkBuildingDomain[] = [];
      let from = 0;
      const pageSize = 1000;
      
      // Fetch all domains in batches
      while (true) {
        const { data, error, count } = await supabase
          .from('os_lb_domains')
          .select('*', { count: 'exact' })
          .eq('brand_id', brandId)
          .order('domain_name', { ascending: true })
          .range(from, from + pageSize - 1);

        if (error) throw error;
        
        if (data) {
          allData = [...allData, ...data];
        }
        
        // If we got less than pageSize, we've reached the end
        if (!data || data.length < pageSize) break;
        
        from += pageSize;
      }
      
      return allData as LinkBuildingDomain[];
    },
    enabled: !!brandId,
  });

  const selectedDomains = allDomains.filter(d => selectedDomainIds.includes(d.id));

  const filteredDomains = allDomains.filter(domain => 
    domain.domain_name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleSelect = (domainId: string) => {
    const isSelected = selectedDomainIds.includes(domainId);
    if (isSelected) {
      onChange(selectedDomainIds.filter(id => id !== domainId));
    } else {
      onChange([...selectedDomainIds, domainId]);
    }
  };

  const handleRemove = (domainId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(selectedDomainIds.filter(id => id !== domainId));
  };

  const handleOpenAddDialog = () => {
    setOpen(false);
    setShowAddDialog(true);
  };

  const handleAddNewDomain = async () => {
    if (!newDomain.domain_name.trim() || !session?.user?.id) return;

    try {
      const result = await createDomain.mutateAsync({
        brand_id: brandId,
        user_id: session.user.id,
        domain_name: newDomain.domain_name.trim(),
        traffic: newDomain.traffic ? parseInt(newDomain.traffic) : undefined,
        dr: newDomain.dr ? parseInt(newDomain.dr) : undefined,
      });

      if (result) {
        onChange([...selectedDomainIds, result.id]);
      }

      setNewDomain({ domain_name: '', traffic: '', dr: '' });
      setShowAddDialog(false);
      setOpen(true);
    } catch (error) {
      console.error('Error adding domain:', error);
    }
  };

  return (
    <>
      <Button
        variant="outline"
        role="combobox"
        aria-expanded={open}
        className="w-full justify-between"
        type="button"
        onClick={() => setOpen(true)}
      >
        {selectedDomains.length > 0 ? (
          <div className="flex flex-wrap gap-1">
            {selectedDomains.map((domain) => (
              <Badge key={domain.id} variant="secondary" className="mr-1">
                {domain.domain_name}
                <button
                  className="ml-1 hover:text-foreground"
                  onClick={(e) => handleRemove(domain.id, e)}
                  type="button"
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            ))}
          </div>
        ) : (
          "Select domains..."
        )}
        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
      </Button>

      {/* Selection Dialog (simpler like Brand Selector) */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitleWithBug 
              popupName="Select Domains" 
              popupContext="/link-building/partners" 
              isOpen={open}
            >
              Select from the {allDomains.length} Domains
            </DialogTitleWithBug>
          </DialogHeader>
          <div className="space-y-2">
            <Input
              placeholder="Search domains..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="h-9"
            />
          </div>
          <div className="max-h-72 overflow-auto border rounded-md">
            {filteredDomains.length === 0 ? (
              <div className="p-4 text-center">
                <p className="text-sm text-muted-foreground mb-2">
                  {allDomains.length === 0 ? 'No domains available' : 'No domains found'}
                </p>
                <Button size="sm" variant="outline" onClick={() => setShowAddDialog(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add New Domain
                </Button>
              </div>
            ) : (
              <div className="p-1">
                {filteredDomains.map((domain) => (
                  <div
                    key={domain.id}
                    className="flex items-center px-2 py-2 hover:bg-accent rounded-sm cursor-pointer"
                    onClick={() => handleSelect(domain.id)}
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        selectedDomainIds.includes(domain.id) ? "opacity-100" : "opacity-0"
                      )}
                    />
                    <div className="flex-1">
                      <div className="text-sm">{domain.domain_name}</div>
                      {(domain.traffic || domain.dr) && (
                        <div className="text-xs text-muted-foreground">
                          {domain.traffic && `Traffic: ${domain.traffic.toLocaleString()}`}
                          {domain.traffic && domain.dr && ' | '}
                          {domain.dr && `DR: ${domain.dr}`}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="flex items-center justify-between">
            <Button variant="ghost" type="button" onClick={() => setShowAddDialog(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Add New Domain
            </Button>
            <DialogFooter>
              <Button variant="outline" type="button" onClick={() => setOpen(false)}>
                Done
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitleWithBug 
              popupName="Add New Domain" 
              popupContext="/link-building/partners" 
              isOpen={showAddDialog}
            >
              Add New Domain
            </DialogTitleWithBug>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="new_domain_name">Domain Name *</Label>
              <Input
                id="new_domain_name"
                value={newDomain.domain_name}
                onChange={(e) => setNewDomain({ ...newDomain, domain_name: e.target.value })}
                placeholder="example.com"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="new_traffic">Traffic</Label>
                <Input
                  id="new_traffic"
                  type="number"
                  value={newDomain.traffic}
                  onChange={(e) => setNewDomain({ ...newDomain, traffic: e.target.value })}
                  placeholder="Monthly traffic"
                />
              </div>
              <div>
                <Label htmlFor="new_dr">DR</Label>
                <Input
                  id="new_dr"
                  type="number"
                  value={newDomain.dr}
                  onChange={(e) => setNewDomain({ ...newDomain, dr: e.target.value })}
                  placeholder="0-100"
                  min="0"
                  max="100"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowAddDialog(false);
              setNewDomain({ domain_name: '', traffic: '', dr: '' });
            }}>
              Cancel
            </Button>
            <Button onClick={handleAddNewDomain} disabled={!newDomain.domain_name.trim()}>
              Add Domain
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
